import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MapPin, Phone, Mail } from 'lucide-react';
import { fetchAddresses } from '../store/feature/addressSlice';
import { fetchPhones } from '../store/feature/phoneSlice';
import { AppDispatch, RootState } from '../store';
import Logo from "../images/Iyblogo2photoroom.png";

function Footer() {
  const dispatch = useDispatch<AppDispatch>();
  const { addresses } = useSelector((state: RootState) => state.address);
  const { phones, loading, error } = useSelector((state: RootState) => state.phone);

  useEffect(() => {
    dispatch(fetchAddresses());
    dispatch(fetchPhones());
  }, [dispatch]);

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Logo Section */}
          <div className="flex flex-col items-center md:items-center space-y-4">
            <img 
              src={Logo} 
              alt="İstanbul Yerbilimleri Logo" 
              className="h-16 w-auto object-contain"
            />
            <p className="text-xl text-gray-400 text-center">
              İstanbul Yer Bilimleri
            </p>
          </div>

          {/* Addresses Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white text-center md:text-left">
              Adreslerimiz
            </h3>
            <div className="space-y-3 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800">
              {addresses.map((address: { id: number; value: string }) => (
                <a
                  key={address.id}
                  href={`https://www.google.com/maps?q=${encodeURIComponent(address.value)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-2 group hover:text-white transition-colors duration-200"
                >
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0 text-blue-400 group-hover:text-blue-300" />
                  <span className="text-sm">{address.value}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white text-center md:text-left">
              İletişim Bilgilerimiz
            </h3>
            <div className="space-y-3">
              {/* Phone Numbers */}
              {loading ? (
                <p className="text-sm text-gray-400">Yükleniyor...</p>
              ) : error ? (
                <p className="text-sm text-red-400">{error}</p>
              ) : phones.length === 0 ? (
                <p className="text-sm text-gray-400">Telefon numarası bulunamadı</p>
              ) : (
                phones.map((phone: { id: number; value: string }) => (
                  <a
                    key={phone.id}
                    href={`tel:${phone.value}`}
                    className="flex items-center space-x-2 group hover:text-white transition-colors duration-200"
                  >
                    <Phone className="h-5 w-5 text-blue-400 group-hover:text-blue-300" />
                    <span className="text-sm">{phone.value}</span>
                  </a>
                ))
              )}
              
              {/* Email */}
              <a
                href="mailto:istanbulyerbilimleri@gmail.com"
                className="flex items-center space-x-2 group hover:text-white transition-colors duration-200"
              >
                <Mail className="h-5 w-5 text-blue-400 group-hover:text-blue-300" />
                <span className="text-sm">istanbulyerbilimleri@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col items-center space-y-2">
            <a 
              href="https://cortexsoftdev.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent hover:from-blue-300 hover:to-blue-500 transition-all duration-300"
            >
              CortexSoft
            </a>
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;